import React, { useCallback } from 'react';
import { gql } from '@apollo/client';
import { PromoBanner } from '@moda/om';
import md5 from 'md5';
import { Link } from '../Link';
import {
  SitePromoBannerFragment,
  SitePromoBannerFragment_targetBuckets_modules_SinglePumoModule as SinglePumoModuleType,
  SitePromoBannerFragment_targetBuckets_modules_SinglePumoModule_slides as SinglePumoModuleSlide
} from '../../generated/types/SitePromoBannerFragment';
import { stripHTMLTags } from '../NormalizePumoStyles';
import { tc } from '../../lib/trackingContext';
import { Crossfader } from '../Crossfader';
import { SitePromoBannerCountrySelector } from './SitePromoBannerCountrySelector';
import { SitePromoBannerLink } from './SitePromoBannerLink';

import './SitePromoBanner.scss';

export const SITE_PROMO_BANNER_FRAGMENT = gql`
  fragment SitePromoBannerFragment on DisplayPage {
    targetBuckets {
      name
      modules {
        ... on SinglePumoModule {
          slides {
            ctaUrl
            slideText {
              ctaText
              taglineText {
                body
              }
            }
          }
        }
      }
    }
  }
`;

const SITE_PROMO_BANNER_FALLBACK_MESSAGE =
  'Free Shipping & Returns in the U.S. | Use Code NEWTOMODA for 10% Off Your First Full-Price Purchase';

const BANNER_DURATION_MS = 4000;
const BANNER_TRANSITION_MS = 1400;

interface Props {
  displayPage?: SitePromoBannerFragment | null;
}

export const SitePromoBanner: React.FC<Props> = ({ displayPage }) => {
  const bannerSlides: SinglePumoModuleSlide[] | null = (
    displayPage?.targetBuckets.find(({ name }) => name === 'banner')
      ?.modules[0] as SinglePumoModuleType
  )?.slides;

  const trackHrefClick = useCallback((taglineText: string, promoId: string) => {
    tc.track('Promotion Clicked', {
      addToTrackingEvent: {
        position: 'promo_banner',
        name: taglineText,
        promotion_id: promoId,
        label: promoId
      }
    });
  }, []);

  return (
    <PromoBanner role="banner" className="SitePromoBanner" data-test-id="site-promo-banner">
      <div>
        <SitePromoBannerCountrySelector />
      </div>

      <div>
        {bannerSlides && bannerSlides.length > 0 ? (
          <Crossfader
            className="SitePromoBanner__cell"
            duration={BANNER_DURATION_MS}
            transition={BANNER_TRANSITION_MS}
            active={bannerSlides.length > 1}
          >
            {bannerSlides.map(slide => (
              <Link
                key={slide?.slideText?.taglineText?.body || SITE_PROMO_BANNER_FALLBACK_MESSAGE}
                className="SitePromoBanner__cta"
                to={slide?.ctaUrl || '#'}
                onClick={() =>
                  trackHrefClick(
                    slide?.slideText?.taglineText?.body || '',
                    slide?.slideText?.ctaText || md5(slide?.slideText?.taglineText?.body || '')
                  )
                }
                title={stripHTMLTags(
                  slide?.slideText?.taglineText?.body || SITE_PROMO_BANNER_FALLBACK_MESSAGE
                )}
                dangerouslySetInnerHTML={{
                  __html: stripHTMLTags(
                    slide?.slideText?.taglineText?.body || SITE_PROMO_BANNER_FALLBACK_MESSAGE
                  )
                }}
              />
            ))}
          </Crossfader>
        ) : (
          <Link className="SitePromoBanner__cta" to="#" title={SITE_PROMO_BANNER_FALLBACK_MESSAGE}>
            {SITE_PROMO_BANNER_FALLBACK_MESSAGE}
          </Link>
        )}
      </div>

      <div>
        <SitePromoBannerLink />
      </div>
    </PromoBanner>
  );
};
